import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Users, Layers, Calendar, Search, Filter, Zap, CheckCircle2, Building2, UserPlus, Loader2, ClipboardCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/blocks/page-header';
import { WorkflowCreationDialog } from '@/components/ApprovalStageWizard/WorkflowCreationDialog';
import { WorkflowDetailsPage } from '@/components/ApprovalStageWizard/WorkflowDetailsPage';
import { WorkflowAssignDialog } from '@/components/WorkflowAssignDialog';
import type { 
  Workflow, 
  WorkflowCreationData, 
  StageData,
  WorkflowStage,
} from '@/components/ApprovalStageWizard/types';
import { toast } from 'sonner';
import { initializeMockData } from '@/data/mockData';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import * as workflowService from '@/services/workflowService';
import { bulkAssignWorkflow } from '@/services/workflowAssignmentService';
import { getAllEmployees } from '@/services/employeeService';
import type { ApprovalWorkflow as BackendApprovalWorkflow, CreateWorkflowRequest, UpdateWorkflowRequest } from '@/types/workflow';
import type { BulkAssignWorkflowRequest } from '@/types/workflow-assignment';
import type { Employee } from '@/types/timesheet';

type ApprovalStatus = 'Active' | 'Draft' | 'Inactive';

interface ApprovalWorkflow {
  id: string;
  name: string;
  status: ApprovalStatus;
  stages: number;
  assignedEmployees: number;
  lastModified: Date;
}

// Mock data for demo mode
const MOCK_APPROVALS: ApprovalWorkflow[] = [
  {
    id: '1',
    name: 'Standard Timesheet Approval',
    status: 'Active',
    stages: 2,
    assignedEmployees: 45,
    lastModified: new Date('2024-01-15'),
  },
  {
    id: '2',
    name: 'Manager Timesheet Approval',
    status: 'Active',
    stages: 3,
    assignedEmployees: 12,
    lastModified: new Date('2024-01-14'),
  },
  {
    id: '3',
    name: 'Executive Timesheet Approval',
    status: 'Draft',
    stages: 4,
    assignedEmployees: 0,
    lastModified: new Date('2024-01-12'),
  },
  {
    id: '4',
    name: 'Contractor Timesheet Approval',
    status: 'Active',
    stages: 2,
    assignedEmployees: 18,
    lastModified: new Date('2024-01-10'),
  },
  {
    id: '5',
    name: 'Intern Timesheet Approval',
    status: 'Inactive',
    stages: 1,
    assignedEmployees: 8,
    lastModified: new Date('2024-01-08'),
  },
];

// Helper function to convert mock approvals to workflows
const convertMockApprovalsToWorkflows = (approvals: ApprovalWorkflow[]): Workflow[] => {
  return approvals.map((approval) => {
    const stages: WorkflowStage[] = [];
    for (let i = 1; i <= approval.stages; i++) {
      stages.push({
        id: `${approval.id}-stage-${i}`,
        order: i,
        name: `Stage ${i}`,
        description: `Approval stage ${i}`,
        status: approval.status === 'Active' ? 'configured' : 'draft',
      });
    }
    return {
      id: approval.id,
      name: approval.name,
      status: approval.status.toLowerCase() as 'draft' | 'active' | 'inactive',
      stages,
      createdAt: approval.lastModified,
      updatedAt: approval.lastModified,
      createdBy: 'system',
      assignedToCount: approval.assignedEmployees,
    };
  });
};

// Helper function to map backend approver type to frontend approver type
const mapBackendApproverTypeToFrontend = (backendType: string): 'role' | 'user' | 'rule' => {
  switch (backendType) {
    case 'specific':
      return 'user';
    case 'role':
    case 'manager':
    case 'department_head':
      return 'role';
    case 'custom':
      return 'rule';
    default:
      return 'role';
  }
};

// Helper function to map backend approver role to frontend role type
const mapBackendRoleToFrontend = (backendType: string, backendRole?: string): 'DirectManager' | 'ProjectManager' | 'DepartmentHead' | undefined => {
  if (backendType === 'manager') return 'DirectManager';
  if (backendType === 'department_head') return 'DepartmentHead';
  if (backendRole) {
    switch (backendRole.toLowerCase()) {
      case 'manager':
        return 'DirectManager';
      case 'project_manager':
        return 'ProjectManager';
      case 'department_head':
        return 'DepartmentHead';
      default:
        return 'DirectManager';
    }
  }
  return undefined;
};

// Helper function to convert backend workflow to frontend Workflow type
const mapBackendWorkflowToFrontend = (backendWorkflow: BackendApprovalWorkflow): Workflow => {
  return {
    id: backendWorkflow.id,
    name: backendWorkflow.name,
    description: backendWorkflow.description,
    status: backendWorkflow.isActive ? 'active' : 'inactive',
    stages: backendWorkflow.stages.map((stage, index) => {
      // Get the first approver from the stage (if any)
      const firstApprover = stage.approvers?.[0];
      
      // Parse advanced config JSON if present
      let advancedConfig: {
        smartRules?: {
          rules?: unknown[];
          onApproved?: { type: string; targetStageId?: string; targetStageName?: string };
          onRejected?: { type: string; targetStageId?: string; targetStageName?: string };
          timeout?: { enabled: boolean; days: number; action: string; escalateTo?: string; escalateToName?: string };
        };
        notifications?: {
          onApproved?: unknown[];
          onRejected?: unknown[];
        };
      } | undefined;
      
      if (stage.advancedConfigJson) {
        try {
          advancedConfig = JSON.parse(stage.advancedConfigJson);
        } catch (e) {
          console.warn('Failed to parse advancedConfigJson for stage:', stage.id, e);
        }
      }
      
      // Parse rule config JSON for approver if present
      let ruleDetermined: unknown | undefined;
      if (firstApprover?.ruleConfigJson) {
        try {
          ruleDetermined = JSON.parse(firstApprover.ruleConfigJson);
        } catch (e) {
          console.warn('Failed to parse ruleConfigJson for approver:', firstApprover.id, e);
        }
      }
      
      // Build stageData from backend approver info and advanced config
      // IMPORTANT: Always populate stageData to preserve rules when updating other stages
      const stageData: StageData = {
        basicDetails: {
          stageName: stage.name,
          description: stage.description,
        },
        approver: firstApprover ? {
          approverType: mapBackendApproverTypeToFrontend(firstApprover.approverType),
          roleType: mapBackendRoleToFrontend(firstApprover.approverType, firstApprover.approverRole),
          specificUserId: firstApprover.approverId,
          specificUserName: firstApprover.approverName,
          cannotApproveOwn: firstApprover.cannotApproveOwn ?? true,
          ruleDetermined: ruleDetermined as StageData['approver']['ruleDetermined'],
        } : {
          // Default approver when none exists
          approverType: 'role',
          roleType: 'DirectManager',
          cannotApproveOwn: true,
        },
        smartRules: {
          rules: (advancedConfig?.smartRules?.rules ?? []) as StageData['smartRules']['rules'],
          onApproved: (advancedConfig?.smartRules?.onApproved ?? { type: 'continue' }) as StageData['smartRules']['onApproved'],
          onRejected: (advancedConfig?.smartRules?.onRejected ?? { type: 'reject' }) as StageData['smartRules']['onRejected'],
          timeout: advancedConfig?.smartRules?.timeout as StageData['smartRules']['timeout'],
        },
        notifications: {
          onApproved: (advancedConfig?.notifications?.onApproved ?? []) as StageData['notifications']['onApproved'],
          onRejected: (advancedConfig?.notifications?.onRejected ?? []) as StageData['notifications']['onRejected'],
        },
        allowApproverEdit: stage.allowApproverEdit,
      };

      return {
        id: stage.id || `${backendWorkflow.id}-stage-${index + 1}`,
        order: stage.stageOrder,
        name: stage.name,
        description: stage.description,
        status: backendWorkflow.isActive ? 'configured' : 'draft',
        stageData,
      };
    }),
    createdAt: new Date(backendWorkflow.createdAt),
    updatedAt: backendWorkflow.updatedAt ? new Date(backendWorkflow.updatedAt) : new Date(backendWorkflow.createdAt),
    createdBy: backendWorkflow.createdBy,
    assignedToCount: 0, // Backend doesn't return this
  };
};

// Helper function to convert backend workflow to ApprovalWorkflow for list display
const mapBackendWorkflowToApproval = (backendWorkflow: BackendApprovalWorkflow): ApprovalWorkflow => {
  return {
    id: backendWorkflow.id,
    name: backendWorkflow.name,
    status: backendWorkflow.isActive ? 'Active' : 'Inactive',
    stages: backendWorkflow.stages.length,
    assignedEmployees: 0, // Backend doesn't return this
    lastModified: backendWorkflow.updatedAt ? new Date(backendWorkflow.updatedAt) : new Date(backendWorkflow.createdAt),
  };
};

// Helper function to map frontend approver type to backend approver type
const mapApproverTypeToBackend = (frontendType: string): 'specific' | 'role' | 'manager' | 'department_head' | 'custom' => {
  switch (frontendType) {
    case 'user':
      return 'specific';
    case 'role':
      return 'role';
    case 'rule':
      return 'custom';
    case 'manager':
      return 'manager';
    case 'department_head':
      return 'department_head';
    default:
      return 'manager'; // Default to manager if unknown type
  }
};

// Helper function to map frontend role type to backend approver role
const mapRoleTypeToBackend = (roleType?: string): string | undefined => {
  if (!roleType) return undefined;
  switch (roleType) {
    case 'DirectManager':
      return 'manager';
    case 'ProjectManager':
      return 'project_manager';
    case 'DepartmentHead':
      return 'department_head';
    default:
      return roleType.toLowerCase();
  }
};

export const ApprovalTestPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDemo, isLive } = useScreenDataMode('settings-approvals');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreationDialog, setShowCreationDialog] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [newStageIdToEdit, setNewStageIdToEdit] = useState<string | null>(null);
  const [newStageName, setNewStageName] = useState<string | undefined>(undefined);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [workflowToAssign, setWorkflowToAssign] = useState<string | null>(null);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Unified workflows state (single source of truth)
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  
  // Employees for assignment dialog
  const [employees, setEmployees] = useState<Employee[]>([]);

  // Load workflows based on mode
  const loadWorkflows = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (isLive) {
        // Fetch from API in live mode
        const backendWorkflows = await workflowService.getAllWorkflows();
        const frontendWorkflows = backendWorkflows.map(mapBackendWorkflowToFrontend);
        setWorkflows(frontendWorkflows);
      } else {
        // Use mock data in demo mode
        setWorkflows(convertMockApprovalsToWorkflows(MOCK_APPROVALS));
      }
    } catch (err) {
      console.error('Failed to load workflows:', err);
      setError('Failed to load workflows. Please try again.');
      toast.error('Failed to load workflows');
    } finally {
      setIsLoading(false);
    }
  }, [isLive]);

  // Load employees based on mode
  const loadEmployees = useCallback(async () => {
    try {
      if (isLive) {
        const apiEmployees = await getAllEmployees();
        setEmployees(apiEmployees);
      } else {
        setEmployees(initializeMockData().employees);
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
      // Fall back to mock data if API fails
      setEmployees(initializeMockData().employees);
    }
  }, [isLive]);

  // Load data on mount and when mode changes
  useEffect(() => {
    loadWorkflows();
    loadEmployees();
  }, [loadWorkflows, loadEmployees]);

  // Listen for company-changed event to refetch employees when switching companies
  // This ensures employees are fetched from the correct integration source (local DB vs external API)
  useEffect(() => {
    const handleCompanyChange = () => {
      if (isLive) {
        loadEmployees();
      }
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => window.removeEventListener('company-changed', handleCompanyChange);
  }, [isLive, loadEmployees]);

  // Compute assigned employee counts per workflow from employees data
  const workflowAssignmentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    employees.forEach((emp) => {
      if (emp.approvalWorkflowId) {
        counts[emp.approvalWorkflowId] = (counts[emp.approvalWorkflowId] || 0) + 1;
      }
    });
    return counts;
  }, [employees]);

  // Derive approvals from workflows for display (single source of truth)
  const approvals: ApprovalWorkflow[] = useMemo(() => {
    return workflows.map((workflow) => ({
      id: workflow.id,
      name: workflow.name,
      status: (workflow.status.charAt(0).toUpperCase() + workflow.status.slice(1)) as ApprovalStatus,
      stages: workflow.stages.length,
      assignedEmployees: workflowAssignmentCounts[workflow.id] || 0,
      lastModified: workflow.updatedAt,
    }));
  }, [workflows, workflowAssignmentCounts]);

  // Filter and search logic
  // Only showing workflows with Active, Inactive, and Draft statuses
  const filteredApprovals = useMemo(() => {
    return approvals.filter((approval) => {
      // Only show allowed statuses (Active, Inactive, Draft)
      const allowedStatuses: ApprovalStatus[] = ['Active', 'Inactive', 'Draft'];
      if (!allowedStatuses.includes(approval.status)) {
        return false;
      }

      // Filter by status
      const matchesStatus = statusFilter === 'all' || approval.status === statusFilter;

      // Filter by search query
      const matchesSearch = approval.name.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesStatus && matchesSearch;
    });
  }, [approvals, statusFilter, searchQuery]);

  const getStatusBadgeVariant = (status: ApprovalStatus) => {
    switch (status) {
      case 'Active':
        return 'default';
      case 'Draft':
        return 'secondary';
      case 'Inactive':
        return 'outline';
    }
  };

  const getStatusColor = (status: ApprovalStatus) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500 hover:bg-green-600';
      case 'Draft':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'Inactive':
        return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  const formatDate = (date: Date): string => {
    const now = new Date();
    // Use floor instead of ceil to avoid showing "1 day ago" for same-day items
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
  };

  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const handleTemplateClick = (templateName: string) => {
    console.log('🚀 Template clicked:', templateName);
    setSelectedTemplate(templateName);
    setShowCreationDialog(true);
  };

  const handleNewWorkflow = () => {
    setSelectedTemplate(null);
    setShowCreationDialog(true);
  };

  const getTemplateStages = (templateName: string): WorkflowStage[] => {
    const timestamp = Date.now();
    
    switch (templateName) {
      case 'Single Manager':
        return [
          {
            id: `stage-${timestamp}-0`,
            name: 'Manager Review',
            description: 'Direct manager reviews and approves timesheet',
            order: 1,
            status: 'configured',
            stageData: {
              basicDetails: {
                stageName: 'Manager Review',
                description: 'Direct manager reviews and approves timesheet',
              },
              approver: {
                approverType: 'role',
                roleType: 'DirectManager',
                cannotApproveOwn: true,
              },
              smartRules: {
                rules: [],
                onApproved: {
                  type: 'continue',
                },
                onRejected: {
                  type: 'reject',
                },
              },
              notifications: {
                onApproved: [],
                onRejected: [],
              },
            },
            ruleCount: {
              preApproval: 0,
              postApproval: 0,
            },
          },
        ];
      
      case 'Two-Stage':
        return [
          {
            id: `stage-${timestamp}-0`,
            name: 'Manager Review',
            description: 'Direct manager reviews timesheet',
            order: 1,
            status: 'configured',
            stageData: {
              basicDetails: {
                stageName: 'Manager Review',
                description: 'Direct manager reviews timesheet',
              },
              approver: {
                approverType: 'role',
                roleType: 'DirectManager',
                cannotApproveOwn: true,
              },
              smartRules: {
                rules: [],
                onApproved: {
                  type: 'continue',
                },
                onRejected: {
                  type: 'reject',
                },
              },
              notifications: {
                onApproved: [],
                onRejected: [],
              },
            },
            ruleCount: {
              preApproval: 0,
              postApproval: 0,
            },
          },
          {
            id: `stage-${timestamp}-1`,
            name: 'Department Head Approval',
            description: 'Department head provides final approval',
            order: 2,
            status: 'configured',
            stageData: {
              basicDetails: {
                stageName: 'Department Head Approval',
                description: 'Department head provides final approval',
              },
              approver: {
                approverType: 'role',
                roleType: 'DepartmentHead',
                cannotApproveOwn: true,
              },
              smartRules: {
                rules: [],
                onApproved: {
                  type: 'gotoEnd',
                },
                onRejected: {
                  type: 'reject',
                },
              },
              notifications: {
                onApproved: [],
                onRejected: [],
              },
            },
            ruleCount: {
              preApproval: 0,
              postApproval: 0,
            },
          },
        ];
      
      case 'Auto-Approve':
        return []; // No stages for auto-approve
      
      default:
        return [];
    }
  };

  const handleCreateWorkflow = async (data: WorkflowCreationData) => {
    const now = new Date();

    if (isLive) {
      try {
        // Get stages from template if one was selected
        const templateStages = selectedTemplate ? getTemplateStages(selectedTemplate) : [];
        
        // Convert template stages to API format, or use default stage if no template
        const apiStages: CreateWorkflowRequest['stages'] = templateStages.length > 0
          ? templateStages.map((stage, index) => {
              const stageData = stage.stageData;
              
              // Map approver type from frontend to backend format
              const approverType = stageData?.approver 
                ? mapApproverTypeToBackend(stageData.approver.approverType)
                : 'manager' as const;
              
              // Build approver object
              const approverObj: CreateWorkflowRequest['stages'][0]['approvers'][0] = {
                approverType,
                approverOrder: 1,
                cannotApproveOwn: stageData?.approver?.cannotApproveOwn ?? true,
              };
              
              // Add approver role if applicable
              if (stageData?.approver?.roleType) {
                approverObj.approverRole = mapRoleTypeToBackend(stageData.approver.roleType);
              }
              
              // Add specific user ID if applicable
              if (stageData?.approver?.specificUserId) {
                approverObj.approverId = stageData.approver.specificUserId;
              }
              
              // Build advanced config JSON from stageData
              const advancedConfig = stageData ? {
                smartRules: {
                  rules: stageData.smartRules.rules,
                  onApproved: stageData.smartRules.onApproved,
                  onRejected: stageData.smartRules.onRejected,
                  timeout: stageData.smartRules.timeout,
                },
                notifications: stageData.notifications,
              } : undefined;
              
              return {
                name: stage.name,
                description: stage.description,
                stageOrder: index + 1,
                approvalType: 'any' as const,
                requiredApprovals: 1,
                allowApproverEdit: stageData?.allowApproverEdit ?? false,
                advancedConfigJson: advancedConfig ? JSON.stringify(advancedConfig) : undefined,
                approvers: [approverObj],
              };
            })
          : [{
              name: 'Initial Stage',
              description: 'Default approval stage',
              stageOrder: 1,
              approvalType: 'any' as const,
              requiredApprovals: 1,
              approvers: [{
                approverType: 'manager' as const,
                approverOrder: 1,
              }],
            }];
        
        // Create workflow via API
        const createRequest: CreateWorkflowRequest = {
          name: data.name,
          description: data.description,
          isActive: false, // Start as inactive (draft)
          isDefault: false,
          stages: apiStages,
        };

        const createdWorkflow = await workflowService.createWorkflow(createRequest);
        const frontendWorkflow = mapBackendWorkflowToFrontend(createdWorkflow);
        
        setWorkflows([...workflows, frontendWorkflow]);
        setShowCreationDialog(false);
        setSelectedWorkflowId(frontendWorkflow.id);
        setSelectedTemplate(null);
        
        const stageMessage = templateStages.length > 0 
          ? `with ${templateStages.length} pre-configured stage${templateStages.length > 1 ? 's' : ''}` 
          : 'Add stages to get started';
        toast.success(`Workflow "${data.name}" created successfully ${stageMessage}!`);
      } catch (err) {
        console.error('Failed to create workflow:', err);
        toast.error('Failed to create workflow. Please try again.');
      }
    } else {
      // Demo mode - create locally with template support
      const workflowId = `wf-${Date.now()}`;
      
      // Get stages from template if one was selected
      const stages = selectedTemplate ? getTemplateStages(selectedTemplate) : [];
      
      const newWorkflow: Workflow = {
        id: workflowId,
        name: data.name,
        description: data.description,
        status: 'draft',
        stages: stages,
        createdAt: now,
        updatedAt: now,
        createdBy: 'current-user',
        assignedToCount: 0,
      };

      setWorkflows([...workflows, newWorkflow]);
      setShowCreationDialog(false);
      setSelectedWorkflowId(workflowId);
      setSelectedTemplate(null);
      
      const stageMessage = stages.length > 0 
        ? `with ${stages.length} pre-configured stage${stages.length > 1 ? 's' : ''}` 
        : 'Add stages to get started';
      toast.success(`Workflow "${data.name}" created successfully ${stageMessage}!`);
    }
  };

  const handleUpdateStage = async (stageId: string, stageData: StageData) => {
    if (!selectedWorkflowId) return;

    // Get the current workflow BEFORE updating state to avoid stale closure issues
    const currentWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!currentWorkflow) return;

    // Build the updated workflow object
    const updatedWorkflow: Workflow = {
      ...currentWorkflow,
      stages: currentWorkflow.stages.map((stage) => {
        if (stage.id !== stageId) return stage;

        // Count rules
        const preApprovalRules = stageData.smartRules.rules.filter(
          (r) => r.executionTiming === 'pre-approval'
        ).length;
        const postApprovalRules = stageData.smartRules.rules.filter(
          (r) => r.executionTiming === 'post-approval'
        ).length;

        return {
          ...stage,
          name: stageData.basicDetails.stageName,
          description: stageData.basicDetails.description,
          status: 'configured' as const,
          stageData,
          ruleCount: {
            preApproval: preApprovalRules,
            postApproval: postApprovalRules,
          },
        };
      }),
      updatedAt: new Date(),
    };

    // Update local state for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => workflow.id === selectedWorkflowId ? updatedWorkflow : workflow)
    );

    // Persist to backend in live mode
    if (isLive) {
      try {
        // Build update request using the updated workflow object (not stale closure)
        const updateRequest: UpdateWorkflowRequest = {
          name: updatedWorkflow.name,
          description: updatedWorkflow.description,
          isActive: updatedWorkflow.status === 'active',
          isDefault: false,
          stages: updatedWorkflow.stages.map((stage) => {
            const isUpdatedStage = stage.id === stageId;
            const stageDataToUse = isUpdatedStage ? stageData : stage.stageData;
            
            // Map approver from stage data
            const approverType = stageDataToUse?.approver 
              ? mapApproverTypeToBackend(stageDataToUse.approver.approverType)
              : 'manager' as const;
            
            const approverObj: { 
              approverType: 'specific' | 'role' | 'manager' | 'department_head' | 'custom';
              approverId?: string;
              approverRole?: string;
              approverOrder: number;
            } = {
              approverType,
              approverOrder: 1,
            };
            
            if (stageDataToUse?.approver) {
              if (stageDataToUse.approver.specificUserId) {
                approverObj.approverId = stageDataToUse.approver.specificUserId;
              }
              if (stageDataToUse.approver.roleType) {
                approverObj.approverRole = mapRoleTypeToBackend(stageDataToUse.approver.roleType);
              }
            }

            // Use undefined for temporary stage IDs so backend generates proper GUIDs
            const backendStageId = stage.id.startsWith('temp-') ? undefined : stage.id;

            // Build advanced config JSON from stageData
            const advancedConfig = stageDataToUse ? {
              smartRules: {
                rules: stageDataToUse.smartRules.rules,
                onApproved: stageDataToUse.smartRules.onApproved,
                onRejected: stageDataToUse.smartRules.onRejected,
                timeout: stageDataToUse.smartRules.timeout,
              },
              notifications: stageDataToUse.notifications,
            } : undefined;

            return {
              id: backendStageId,
              name: isUpdatedStage ? stageData.basicDetails.stageName : stage.name,
              description: isUpdatedStage ? stageData.basicDetails.description : stage.description,
              stageOrder: stage.order,
              approvalType: 'any' as const,
              requiredApprovals: 1,
              allowApproverEdit: stageDataToUse?.allowApproverEdit ?? false,
              advancedConfigJson: advancedConfig ? JSON.stringify(advancedConfig) : undefined,
              approvers: [{
                ...approverObj,
                cannotApproveOwn: stageDataToUse?.approver?.cannotApproveOwn ?? true,
                ruleConfigJson: stageDataToUse?.approver?.ruleDetermined 
                  ? JSON.stringify(stageDataToUse.approver.ruleDetermined) 
                  : undefined,
              }],
            };
          }),
        };

        await workflowService.updateWorkflow(selectedWorkflowId, updateRequest);
        toast.success('Stage configuration saved!');
        
        // Reload workflow to get canonical IDs from backend (especially for new stages)
        const hasNewStages = updatedWorkflow.stages.some((s) => s.id.startsWith('temp-'));
        if (hasNewStages) {
          await loadWorkflows();
        }
      } catch (err) {
        console.error('Failed to update workflow:', err);
        toast.error('Failed to save stage configuration to server');
        // Revert local state on error
        loadWorkflows();
      }
    } else {
      toast.success('Stage configuration saved!');
    }
  };

  const handleBackToList = () => {
    setSelectedWorkflowId(null);
  };

  const handleViewWorkflow = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
  };

  const handleAssignWorkflow = (workflowId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setWorkflowToAssign(workflowId);
    setAssignDialogOpen(true);
  };

  const handleAssignClose = (open: boolean) => {
    setAssignDialogOpen(open);
    if (!open) {
      setWorkflowToAssign(null);
    }
  };

  const handleAssign = async (
    workflowId: string,
    assignmentType: 'all' | 'selected',
    selectedEmployeeIds?: number[]
  ) => {
    try {
      if (isLive) {
        // Use bulkAssignWorkflow from workflowAssignmentService in live mode
        const employeeIdsToAssign = assignmentType === 'all' 
          ? employees.map(e => e.id)
          : selectedEmployeeIds || [];

        if (employeeIdsToAssign.length === 0) {
          toast.error('No employees selected for assignment');
          return;
        }

        const request: BulkAssignWorkflowRequest = {
          employeeIds: employeeIdsToAssign,
          workflowId: workflowId,
          reason: 'Assigned via Approval Workflows page',
        };

        const result = await bulkAssignWorkflow(request);

        // Show success toast if at least one assignment succeeded
        // This handles the case where some assignments succeed but others fail
        if (result.successCount > 0) {
          toast.success(`Successfully assigned workflow to ${result.successCount} employee(s)`);
          if (result.failedCount > 0) {
            toast.warning(`${result.failedCount} assignment(s) failed`);
          }
          // Reload employees to update assignment counts in UI
          await loadEmployees();
        } else {
          // Only show error if ALL assignments failed
          toast.error(result.message || 'Failed to assign workflow');
        }
      } else {
        // Demo mode - use mock assignment
        const result = await workflowService.assignWorkflow(workflowId, assignmentType, selectedEmployeeIds);
        toast.success(`Successfully assigned workflow to ${result.assignedCount} employee(s)`);
      }
    } catch (error) {
      console.error('Failed to assign workflow:', error);
      toast.error('Failed to assign workflow. Please try again.');
    }
  };

  const handleAddStage = (stageName?: string) => {
    if (!selectedWorkflowId) return;

    const selectedWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!selectedWorkflow) return;

    const newStageOrder = selectedWorkflow.stages.length + 1;
    // Use a temporary ID prefix for new stages - will be replaced by backend-generated GUID
    const tempStageId = `temp-stage-${Date.now()}-${newStageOrder}`;
    const newStage: WorkflowStage = {
      id: tempStageId,
      order: newStageOrder,
      name: stageName || `Stage ${newStageOrder}`, // Use provided name or default
      description: '',
      status: 'draft',
    };

    setWorkflows((prev) =>
      prev.map((workflow) => {
        if (workflow.id !== selectedWorkflowId) return workflow;

        return {
          ...workflow,
          stages: [...workflow.stages, newStage],
          updatedAt: new Date(),
        };
      })
    );

    // Open the configuration wizard immediately with pre-filled name
    setNewStageIdToEdit(tempStageId);
    setNewStageName(stageName); // Pass the stage name to pre-fill in wizard
  };

  const handleUpdateWorkflowStatus = async (workflowId: string, newStatus: Workflow['status']) => {
    // Update local state first for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => {
        if (workflow.id !== workflowId) return workflow;

        return {
          ...workflow,
          status: newStatus,
          updatedAt: new Date(),
        };
      })
    );

    // Persist to backend in live mode
    if (isLive) {
      try {
        if (newStatus === 'active') {
          await workflowService.activateWorkflow(workflowId);
          toast.success('Workflow activated');
        } else if (newStatus === 'inactive' || newStatus === 'draft') {
          // Backend only supports isActive boolean, so both 'inactive' and 'draft' map to isActive=false
          await workflowService.deactivateWorkflow(workflowId);
          toast.success(newStatus === 'draft' ? 'Workflow set to draft' : 'Workflow deactivated');
        }
      } catch (err) {
        console.error('Failed to update workflow status:', err);
        toast.error('Failed to update workflow status');
        // Revert local state on error
        loadWorkflows();
      }
    }
  };

  // Handle saving draft to backend (without closing wizard)
  const handleSaveDraft = async (stageId: string, stageData: StageData) => {
    if (!selectedWorkflowId) return;

    // Get the current workflow
    const currentWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!currentWorkflow) return;

    // Build the updated workflow object (similar to handleUpdateStage but without marking as configured)
    const updatedWorkflow: Workflow = {
      ...currentWorkflow,
      stages: currentWorkflow.stages.map((stage) => {
        if (stage.id !== stageId) return stage;

        return {
          ...stage,
          name: stageData.basicDetails.stageName,
          description: stageData.basicDetails.description,
          // Keep status as draft when saving draft
          status: stage.status === 'configured' ? 'configured' : 'draft' as const,
          stageData,
        };
      }),
      updatedAt: new Date(),
    };

    // Update local state for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => workflow.id === selectedWorkflowId ? updatedWorkflow : workflow)
    );

    // Persist to backend in live mode
    if (isLive) {
      // Build update request using the updated workflow object
      const updateRequest: UpdateWorkflowRequest = {
        name: updatedWorkflow.name,
        description: updatedWorkflow.description,
        isActive: updatedWorkflow.status === 'active',
        isDefault: false,
        stages: updatedWorkflow.stages.map((stage) => {
          const isUpdatedStage = stage.id === stageId;
          const stageDataToUse = isUpdatedStage ? stageData : stage.stageData;
          
          // Map approver from stage data
          const approverType = stageDataToUse?.approver 
            ? mapApproverTypeToBackend(stageDataToUse.approver.approverType)
            : 'manager' as const;
          
          const approverObj: { 
            approverType: 'specific' | 'role' | 'manager' | 'department_head' | 'custom';
            approverId?: string;
            approverRole?: string;
            approverOrder: number;
          } = {
            approverType,
            approverOrder: 1,
          };
          
          if (stageDataToUse?.approver) {
            if (stageDataToUse.approver.specificUserId) {
              approverObj.approverId = stageDataToUse.approver.specificUserId;
            }
            if (stageDataToUse.approver.roleType) {
              approverObj.approverRole = mapRoleTypeToBackend(stageDataToUse.approver.roleType);
            }
          }

          // Use undefined for temporary stage IDs so backend generates proper GUIDs
          const backendStageId = stage.id.startsWith('temp-') ? undefined : stage.id;

          // Build advanced config JSON from stageData
          const advancedConfig = stageDataToUse ? {
            smartRules: {
              rules: stageDataToUse.smartRules.rules,
              onApproved: stageDataToUse.smartRules.onApproved,
              onRejected: stageDataToUse.smartRules.onRejected,
              timeout: stageDataToUse.smartRules.timeout,
            },
            notifications: stageDataToUse.notifications,
          } : undefined;

          return {
            id: backendStageId,
            name: isUpdatedStage ? stageData.basicDetails.stageName : stage.name,
            description: isUpdatedStage ? stageData.basicDetails.description : stage.description,
            stageOrder: stage.order,
            approvalType: 'any' as const,
            requiredApprovals: 1,
            allowApproverEdit: stageDataToUse?.allowApproverEdit ?? false,
            advancedConfigJson: advancedConfig ? JSON.stringify(advancedConfig) : undefined,
            approvers: [{
              ...approverObj,
              cannotApproveOwn: stageDataToUse?.approver?.cannotApproveOwn ?? true,
              ruleConfigJson: stageDataToUse?.approver?.ruleDetermined 
                ? JSON.stringify(stageDataToUse.approver.ruleDetermined) 
                : undefined,
            }],
          };
        }),
      };

      await workflowService.updateWorkflow(selectedWorkflowId, updateRequest);
      
      // Reload workflow to get canonical IDs from backend (especially for new stages)
      const hasNewStages = updatedWorkflow.stages.some((s) => s.id.startsWith('temp-'));
      if (hasNewStages) {
        await loadWorkflows();
      }
    }
  };

  const selectedWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
  
  // Hydrate selectedWorkflow with computed assignedToCount from employees data
  const selectedWorkflowWithCount = selectedWorkflow ? {
    ...selectedWorkflow,
    assignedToCount: workflowAssignmentCounts[selectedWorkflow.id] ?? 0,
  } : undefined;

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading workflows...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <p className="text-destructive">{error}</p>
          <Button onClick={loadWorkflows}>Try Again</Button>
        </div>
      </div>
    );
  }

  // If viewing a workflow, show the details page
  if (selectedWorkflowWithCount) {
    return (
      <WorkflowDetailsPage
        workflow={selectedWorkflowWithCount}
        onBack={handleBackToList}
        onUpdateStage={handleUpdateStage}
        onSaveDraft={isLive ? handleSaveDraft : undefined}
        onAddStage={handleAddStage}
        onUpdateWorkflowStatus={handleUpdateWorkflowStatus}
        initialEditingStageId={newStageIdToEdit}
        onClearInitialEditingStageId={() => {
          // In live mode, remove any unsaved temp stages when wizard is closed
          // This prevents UI drift from server state
          if (isLive && newStageIdToEdit && newStageIdToEdit.startsWith('temp-')) {
            setWorkflows((prev) =>
              prev.map((workflow) => {
                if (workflow.id !== selectedWorkflowId) return workflow;
                // Check if the temp stage was saved (has stageData configured)
                const tempStage = workflow.stages.find((s) => s.id === newStageIdToEdit);
                if (tempStage && !tempStage.stageData) {
                  // Stage was never configured, remove it
                  return {
                    ...workflow,
                    stages: workflow.stages.filter((s) => s.id !== newStageIdToEdit),
                  };
                }
                return workflow;
              })
            );
          }
          setNewStageIdToEdit(null);
          setNewStageName(undefined); // Clear stage name when closing wizard
        }}
        initialStageName={newStageName}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <PageHeader
        title="Timesheet Approval Workflows"
        subtitle="Manage and configure timesheet approval workflows for different employee groups"
        size={36}
        showBreadcrumb={false}
        icon={<ClipboardCheck />}
        actions={
          <Button className="flex items-center gap-2" onClick={handleNewWorkflow}>
            <Plus className="h-4 w-4" />
            New Workflow
          </Button>
        }
      />

      {/* Quick Start Templates - Compact Cards */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Quick Start Templates</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">
            Get started quickly with pre-configured workflows
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Template 1: Single Manager */}
            <button
              onClick={() => handleTemplateClick('Single Manager')}
              className="flex items-start gap-3 p-3 rounded-lg border-2 border-dashed hover:border-primary hover:bg-accent transition-all text-left group"
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-sm">Single Manager</h4>
                  <Badge variant="secondary" className="text-xs">1 Stage</Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  Simple one-stage approval by direct manager
                </p>
              </div>
            </button>

            {/* Template 2: Two-Stage */}
            <button
              onClick={() => handleTemplateClick('Two-Stage')}
              className="flex items-start gap-3 p-3 rounded-lg border-2 border-dashed hover:border-primary hover:bg-accent transition-all text-left group"
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-sm">Two-Stage</h4>
                  <Badge variant="secondary" className="text-xs">2 Stages</Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  Manager review followed by department head approval
                </p>
              </div>
            </button>

            {/* Template 3: Auto-Approve */}
            <button
              onClick={() => handleTemplateClick('Auto-Approve')}
              className="flex items-start gap-3 p-3 rounded-lg border-2 border-dashed hover:border-primary hover:bg-accent transition-all text-left group"
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-sm">Auto-Approve</h4>
                  <Badge variant="secondary" className="text-xs">No Review</Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  No approval required - timesheets auto-approve
                </p>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filter Section */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Workflows</p>
                <p className="text-2xl font-bold">
                  {filteredApprovals.filter((a) => a.status === 'Active').length}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  of {approvals.filter((a) => a.status === 'Active').length} total
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <div className="h-6 w-6 rounded-full bg-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Draft Workflows</p>
                <p className="text-2xl font-bold">
                  {filteredApprovals.filter((a) => a.status === 'Draft').length}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  of {approvals.filter((a) => a.status === 'Draft').length} total
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <div className="h-6 w-6 rounded-full bg-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold">
                  {filteredApprovals.reduce((sum, a) => sum + a.assignedEmployees, 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  of {approvals.reduce((sum, a) => sum + a.assignedEmployees, 0)} total
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Approval Workflows Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredApprovals.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-muted-foreground text-lg">No workflows found matching your criteria</p>
            <p className="text-muted-foreground text-sm mt-2">Try adjusting your search or filter settings</p>
          </div>
        ) : (
          filteredApprovals.map((approval) => (
          <Card
            key={approval.id}
            className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary/50"
            onClick={() => handleViewWorkflow(approval.id)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-lg font-semibold leading-tight">
                  {approval.name}
                </CardTitle>
                <Badge
                  variant={getStatusBadgeVariant(approval.status)}
                  className={
                    approval.status === 'Active'
                      ? getStatusColor(approval.status)
                      : approval.status === 'Draft'
                      ? getStatusColor(approval.status)
                      : ''
                  }
                >
                  {approval.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Stages */}
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-primary/10">
                  <Layers className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Stages</p>
                  <p className="font-semibold">{approval.stages}</p>
                </div>
              </div>

              {/* Assigned Employees */}
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-blue-100">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Assigned Employees</p>
                  <p className="font-semibold">{approval.assignedEmployees}</p>
                </div>
              </div>

              {/* Last Modified */}
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-purple-100">
                  <Calendar className="h-4 w-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Last Modified</p>
                  <p className="font-semibold">{formatDate(approval.lastModified)}</p>
                </div>
              </div>

              {/* Assign Button */}
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={(e) => handleAssignWorkflow(approval.id, e)}
                  disabled={approval.status !== 'Active'}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign to Users
                </Button>
              </div>
            </CardContent>
          </Card>
          ))
        )}
      </div>

      {/* Workflow Creation Dialog */}
      <WorkflowCreationDialog
        isOpen={showCreationDialog}
        onClose={() => setShowCreationDialog(false)}
        onCreated={handleCreateWorkflow}
      />

      {/* Workflow Assignment Dialog */}
      {workflowToAssign && (
        <WorkflowAssignDialog
          open={assignDialogOpen}
          onOpenChange={handleAssignClose}
          workflow={(() => {
            const approval = approvals.find((a) => a.id === workflowToAssign);
            if (!approval) return null as unknown as import('@/types/workflow').ApprovalWorkflow;
            return {
              id: approval.id,
              name: approval.name,
              description: '',
              isActive: approval.status === 'Active',
              isDefault: false,
              createdAt: approval.lastModified,
              createdBy: 'system',
              version: 1,
              stages: [],
              totalStages: approval.stages,
              totalApprovers: 0,
              isInUse: approval.assignedEmployees > 0,
            } as unknown as import('@/types/workflow').ApprovalWorkflow;
          })()}
          employees={employees}
          onAssign={handleAssign}
        />
      )}
    </div>
  );
};
